import { useCallback, useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import clsx from 'clsx';
import { pushHostClipTextToActiveMirror } from '@/lib/clipboard/store';

/**
 * Tiny shared "copy to clipboard" affordance.
 *
 * Behaviour:
 *   - Click → writes `text` to the system clipboard, flips the icon
 *     to a check for 1.4 s, then reverts.
 *   - Falls back to a hidden `<textarea>` + `document.execCommand`
 *     for older browsers / unsecured contexts where `navigator.
 *     clipboard.writeText` throws.
 *   - Pure UI; takes no event from the parent. The parent should set
 *     `onClick` handlers on its own row separately if needed
 *     (`stopPropagation` is already wired here so the row click
 *     handler doesn't fire when the user just wants to copy).
 *
 * Visual: matches the existing `btn-ghost btn-xs` density — used
 * inline next to file names, error messages, package names, etc.
 */
export function CopyButton({
  text,
  label,
  size = 'md',
  className,
}: {
  /** Resolved each click; pass a function when the value can change. */
  text: string | (() => string);
  /** Tooltip shown via `title=` and announced to screen readers. */
  label?: string;
  /** `sm` = 12 px icon, `md` = 14 px (default). */
  size?: 'sm' | 'md';
  className?: string;
}): JSX.Element {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1_400);
    return () => window.clearTimeout(id);
  }, [copied]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const value = typeof text === 'function' ? text() : text;
      if (!value) return;
      void writeClipboard(value).then((ok) => {
        if (ok) setCopied(true);
      });
    },
    [text]
  );

  const iconClass = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label ?? '复制'}
      title={copied ? '已复制' : label ?? '复制'}
      className={clsx(
        'inline-flex shrink-0 items-center justify-center rounded p-0.5 text-ink-dim transition hover:bg-bg-soft hover:text-ink',
        copied && 'text-emerald-500 hover:text-emerald-500',
        className
      )}
    >
      {copied ? <Check className={iconClass} /> : <Copy className={iconClass} />}
    </button>
  );
}

async function writeClipboard(value: string): Promise<boolean> {
  const ok = await writeClipboardRaw(value);
  // Forward every successful SPA-internal copy into the active mirror
  // bucket so the user sees their PC-side history grow. The helper
  // is a no-op when no mirror is open (intentional: "不开镜像不偷
  // 读 PC 剪贴板"), so this is safe to call unconditionally here.
  if (ok) pushHostClipTextToActiveMirror(value);
  return ok;
}

async function writeClipboardRaw(value: string): Promise<boolean> {
  // Prefer the async clipboard API. It's the only one that works on
  // top-level navigation triggers + iOS Safari.
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // fall through to legacy path
    }
  }
  // Legacy fallback for unsecured contexts (http://, very old Safari).
  // Creates a hidden textarea, selects, execCommand('copy'), restores
  // focus + selection. Returns false if execCommand also fails so the
  // caller can decide whether to toast.
  if (typeof document === 'undefined') return false;
  try {
    const el = document.createElement('textarea');
    el.value = value;
    el.style.position = 'fixed';
    el.style.left = '-9999px';
    el.setAttribute('readonly', '');
    document.body.appendChild(el);
    const prev = document.activeElement as HTMLElement | null;
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    if (prev && prev.focus) prev.focus();
    return ok;
  } catch {
    return false;
  }
}
